import { FC } from 'react';
import { DualDateRangePicker, Typography } from '@crisellevaldez/speculo-ui';
import { convertDateRangeForDisplay } from '../../utils/dateRangeTimezone';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import { LiveIndicator } from '../common/LiveIndicator';
import { useRotatingMessage, LiveStats } from '../../hooks/dashboard/useRotatingMessage';

interface DateRangeBarProps {
  dateRange: { from: Date | null; to: Date | null };
  onDateRangeChange: (range: { from: Date | null; to: Date | null }) => void;
  timezone?: string;
  liveStats?: LiveStats;
}

export const DateRangeBar: FC<DateRangeBarProps> = ({
  dateRange,
  onDateRangeChange,
  timezone,
  liveStats,
}) => {
  const organization = useOrganizationStore((state) => state.organization);
  const assistantName = organization?.assistant_name || 'Remi';
  const { message, fading } = useRotatingMessage(liveStats, dateRange);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 2xl:mb-6">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <Typography variant="h3" as="h2" className="!text-xl font-semibold">
            Dashboard
          </Typography>
          <span className="inline-flex items-center gap-2 sm:gap-2.5 whitespace-nowrap">
            <LiveIndicator size="md" />
            <span className="text-sm font-semibold text-green-700 dark:text-green-400 tracking-tight">
              {assistantName}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.07em] px-1.5 py-0.5 rounded-md border border-green-600/60 bg-green-100 text-green-800 dark:border-green-400/40 dark:bg-green-400/10 dark:text-green-400 leading-none">
              Live
            </span>
            <span
              className={`hidden lg:inline text-sm font-semibold text-green-700/80 dark:text-green-400/80 transition-opacity duration-300 ${fading ? 'opacity-0' : 'opacity-100'}`}
            >
              {message}
            </span>
          </span>
        </div>
        <span
          className={`lg:hidden block text-xs font-semibold text-green-700/80 dark:text-green-400/80 mt-1 transition-opacity duration-300 ${fading ? 'opacity-0' : 'opacity-100'}`}
        >
          {message}
        </span>
      </div>
      <div className="flex items-center gap-3 self-start sm:self-auto">
        <span className="text-xs text-muted-foreground shrink-0 hidden sm:inline">Filter by</span>
        <div className="w-fit">
          <DualDateRangePicker
            value={convertDateRangeForDisplay(dateRange, timezone)}
            onChange={onDateRangeChange}
            showPresets={true}
            minDate={new Date(2025, 5, 1)}
          />
        </div>
      </div>
    </div>
  );
};
